import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { CheckCircle, Users, CalendarCheck, TrendingUp, Zap } from "lucide-react";
import { motion, useMotionValue, useTransform, animate } from "framer-motion";
import { useEffect, useRef, useState } from "react";

const stats = [
  { icon: Users, value: 120, suffix: "+", label: "Leads / Week" },
  { icon: CalendarCheck, value: 35, suffix: "%", label: "Increase in Bookings" },
  { icon: TrendingUp, value: 2, suffix: "x", label: "ROI Improvement" },
  { icon: Zap, value: 60, prefix: "< ", suffix: "s", label: "Lead Response Time" },
];

const AnimatedCounter = ({ value, prefix = "", suffix = "", delay = 0 }: { value: number; prefix?: string; suffix?: string; delay?: number }) => {
  const [display, setDisplay] = useState(0);
  const ref = useRef<HTMLSpanElement>(null);
  const [hasAnimated, setHasAnimated] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !hasAnimated) {
          setHasAnimated(true);
          const duration = 1.5;
          const startTime = performance.now();
          const step = (now: number) => {
            const elapsed = (now - startTime) / 1000;
            const progress = Math.min(elapsed / duration, 1);
            const eased = 1 - Math.pow(1 - progress, 3);
            setDisplay(Math.round(eased * value));
            if (progress < 1) requestAnimationFrame(step);
          };
          setTimeout(() => requestAnimationFrame(step), delay * 1000);
        }
      },
      { threshold: 0.5 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, [value, delay, hasAnimated]);

  return <span ref={ref}>{prefix}{display}{suffix}</span>;
};

const HeroSection = () => {
  return (
    <section className="relative overflow-hidden bg-background">
      {/* Subtle gradient bg */}
      <div className="absolute inset-0">
        <div className="absolute top-0 right-0 w-[600px] h-[600px] bg-primary/5 rounded-full blur-3xl -translate-y-1/3 translate-x-1/4" />
        <div className="absolute bottom-0 left-0 w-[400px] h-[400px] bg-primary/5 rounded-full blur-3xl translate-y-1/3 -translate-x-1/4" />
      </div>

      <div className="max-w-6xl mx-auto relative z-10 w-full px-4 sm:px-6 lg:px-8 pt-10 pb-0">
        <div className="flex flex-col items-center text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-primary/10 text-primary text-sm font-medium mb-5"
          >
            <span className="w-2 h-2 rounded-full bg-primary animate-pulse" />
            Performance Marketing & Automation Agency
          </motion.div>

          <motion.h1
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.15, duration: 0.7 }}
            className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold text-foreground mb-4 max-w-4xl leading-tight"
          >
            We Help Service Businesses Turn More Leads Into{" "}
            <span className="text-primary">Booked Customers</span>
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.3, duration: 0.7 }}
            className="text-base sm:text-lg text-muted-foreground mb-6 max-w-2xl leading-relaxed"
          >
            Using high-performing ads, conversion-focused funnels, and automated follow-ups — so you stop losing 30–50% of your leads.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.45, duration: 0.7 }}
            className="flex flex-col sm:flex-row gap-3 mb-5"
          >
            <Link to="/contact">
              <Button size="lg" className="px-8 py-5 text-base font-semibold shadow-lg hover:shadow-xl transition-all duration-300">
                Get a Free Funnel Audit
              </Button>
            </Link>
            <a href="#how-it-works">
              <Button size="lg" variant="outline" className="px-8 py-5 text-base font-semibold border-2">
                See How It Works
              </Button>
            </a>
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.6, duration: 0.7 }}
            className="flex flex-col sm:flex-row items-center gap-4 md:gap-6 text-sm text-muted-foreground mb-0"
          >
            {[
              "120+ Leads Generated Weekly",
              "30–50% Increase in Booking Rate",
              "Used by Growing Service Businesses",
            ].map((text, i) => (
              <div key={i} className="flex items-center gap-2">
                <CheckCircle className="h-4 w-4 text-primary" />
                <span>{text}</span>
              </div>
            ))}
          </motion.div>
        </div>
      </div>

      {/* Stats strip integrated into hero */}
      <div className="mt-8 bg-foreground relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-r from-primary/5 via-transparent to-primary/5" />
        <div className="max-w-6xl mx-auto px-4 py-10 relative z-10">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-6">
            {stats.map((stat, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 30, scale: 0.9 }}
                whileInView={{ opacity: 1, y: 0, scale: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: i * 0.12, ease: "easeOut" }}
                whileHover={{ scale: 1.05, y: -4 }}
                className="text-center group cursor-default"
              >
                <div className="inline-flex items-center justify-center w-10 h-10 rounded-xl bg-primary/10 mb-3 group-hover:bg-primary/20 transition-colors duration-300">
                  <stat.icon className="h-5 w-5 text-primary" />
                </div>
                <div className="text-3xl sm:text-4xl font-bold text-primary-foreground mb-1 tracking-tight">
                  <AnimatedCounter value={stat.value} prefix={stat.prefix} suffix={stat.suffix} delay={0.7 + i * 0.15} />
                </div>
                <p className="text-xs sm:text-sm text-primary-foreground/50 font-medium">{stat.label}</p>
                <div className="mt-3 mx-auto w-8 h-0.5 rounded-full bg-primary/20 group-hover:w-12 group-hover:bg-primary/40 transition-all duration-300" />
              </motion.div>
            ))}
          </div>
        </div>
      </div>
    </section>
  );
};

export default HeroSection;